import { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import { useTheme, Box } from '@mui/material';
import type { InvestmentStatus, InvestmentStatusData } from '@inversor-hub/types';
import { investmentStatusLabelMap } from '@/features/investments/util.ts';
import { PreviewOverlay } from './PreviewOverlay.tsx';

interface InvestmentStatusBarChartProps {
  data: InvestmentStatusData[];
  height?: number;
  onStartInvesting?: () => void;
}

const statusColors: Record<InvestmentStatus, string> = {
  PURCHASING: '#ff9800', // Orange
  RENOVATING: '#2196f3', // Blue
  LISTED_FOR_RENT_OR_SALE: '#9c27b0', // Purple
  RENTED: '#4caf50', // Green
  SOLD: '#607d8b', // Blue grey
};

// Sample preview data to show what the chart will look like
const previewData: InvestmentStatusData[] = [
  { status: 'PURCHASING', count: 2, percentage: 25, value: 120000 },
  { status: 'RENOVATING', count: 3, percentage: 37.5, value: 180000 },
  { status: 'LISTED_FOR_RENT_OR_SALE', count: 1, percentage: 12.5, value: 60000 },
  { status: 'RENTED', count: 1, percentage: 12.5, value: 60000 },
  { status: 'SOLD', count: 1, percentage: 12.5, value: 60000 },
];

export default function InvestmentStatusBarChart({
  data,
  height = 350,
  onStartInvesting,
}: InvestmentStatusBarChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const hasData = data.length > 0;
  const chartData = hasData ? data : previewData;

  useEffect(() => {
    if (!chartRef.current) return;

    const chart = echarts.init(chartRef.current);

    const chartDataFormatted = chartData.map((item) => ({
      name: investmentStatusLabelMap[item.status],
      value: item.count,
      itemStyle: {
        color: hasData ? statusColors[item.status] : theme.palette.grey[400],
      },
    }));

    const option = {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: theme.palette.divider,
        borderWidth: 1,
        textStyle: {
          color: theme.palette.text.primary,
          fontSize: 12,
        },
        formatter: (params: echarts.DefaultLabelFormatterCallbackParams[]) => {
          const item = chartData.find((d) => investmentStatusLabelMap[d.status] === params[0].name);
          if (!item) return '';

          return `
            <div style="font-weight: 600; margin-bottom: 8px;">${params[0].name}</div>
            <div style="margin-bottom: 4px;">
              <span style="display: inline-block; width: 10px; height: 10px; background-color: ${
                params[0].color
              }; border-radius: 50%; margin-right: 8px;"></span>
              Inversiones: ${item.count}
            </div>
            <div style="margin-bottom: 4px;">
              Porcentaje: ${item.percentage.toFixed(1)}%
            </div>
            <div>
              Valor: €${item.value.toLocaleString()}
            </div>
          `;
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '10%',
        top: '10%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: chartDataFormatted.map((item) => item.name),
        axisLabel: {
          color: theme.palette.text.secondary,
          fontSize: 11,
          interval: 0,
          rotate: 45,
        },
        axisLine: {
          lineStyle: {
            color: theme.palette.divider,
          },
        },
        axisTick: {
          lineStyle: {
            color: theme.palette.divider,
          },
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          color: theme.palette.text.secondary,
          fontSize: 11,
        },
        axisLine: {
          lineStyle: {
            color: theme.palette.divider,
          },
        },
        axisTick: {
          lineStyle: {
            color: theme.palette.divider,
          },
        },
        splitLine: {
          lineStyle: {
            color: theme.palette.divider,
            type: 'dashed',
          },
        },
      },
      series: [
        {
          name: 'Inversiones',
          type: 'bar',
          data: chartDataFormatted,
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
          barWidth: '60%',
        },
      ],
    };

    chart.setOption(option);

    const handleResize = () => {
      chart.resize();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.dispose();
    };
  }, [chartData, theme, hasData]);

  return (
    <Box sx={{ position: 'relative', width: '100%', height: `${height}px` }}>
      <div ref={chartRef} style={{ width: '100%', height: `${height}px` }} />
      {!hasData && (
        <PreviewOverlay
          title="Estado de Inversiones"
          description="Seguimiento visual del progreso de tus inversiones desde la compra hasta la rentabilización."
          actionText="Ver Inversiones"
          onAction={onStartInvesting}
        />
      )}
    </Box>
  );
}
